import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";

/**
 * Les tons de feedback passent par les tokens sémantiques `*-subtle` / `*-fg`,
 * qui embarquent déjà leur déclinaison sombre. D'où l'absence totale de
 * variante `dark:` ici : c'est le signe que la couche sémantique fait son travail.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 font-medium whitespace-nowrap transition-colors duration-fast ease-standard",
  {
    variants: {
      tone: {
        neutral: "bg-surface-sunken text-fg-muted ring-1 ring-border ring-inset",
        brand: "bg-accent-subtle text-accent-fg",
        success: "bg-success-subtle text-success-fg",
        warning: "bg-warning-subtle text-warning-fg",
        danger: "bg-danger-subtle text-danger-fg",
        info: "bg-info-subtle text-info-fg",
        /** Aplat plein, pour un compteur ou une pastille qui doit ressortir. */
        solid: "bg-accent text-fg-on-accent inset-shadow-raised",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        md: "px-2.5 py-1 text-sm",
      },
      shape: {
        rounded: "rounded-md",
        pill: "rounded-full",
      },
    },
    defaultVariants: { tone: "neutral", size: "sm", shape: "pill" },
  },
);

export interface BadgeProps
  extends ComponentPropsWithRef<"span">,
    VariantProps<typeof badgeVariants> {
  /** Petite pastille de couleur affichée avant le libellé (statut en ligne, catégorie). */
  dot?: boolean;
  /**
   * Ajoute un halo qui se propage autour de la pastille. À réserver à ce qui est
   * réellement en direct (build en cours, utilisateur connecté) : une animation
   * perpétuelle qui ne signale rien devient un parasite visuel.
   */
  pulse?: boolean;
}

/** Étiquette de statut. Purement informative : n'y placez jamais d'interaction. */
export function Badge({ className, tone, size, shape, dot, pulse, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, size, shape }), className)} {...props}>
      {dot && (
        <span className="relative flex size-1.5 shrink-0" aria-hidden="true">
          {pulse && (
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60" />
          )}
          <span className="relative inline-flex size-full rounded-full bg-current opacity-80" />
        </span>
      )}
      {children}
    </span>
  );
}

export { badgeVariants };
